import { useState, useRef, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useVirtualizer } from "@tanstack/react-virtual";
import { HiOutlineX, HiOutlineSearch, HiOutlineExternalLink, HiOutlineCheck, HiOutlineRewind, HiOutlineTag } from "react-icons/hi";
import API from "../api";
import { capture } from "../instrument";
import { useSocket } from "../contexts/SocketContext";
import { IS_BETA_HOST } from "../utils";
import AddExamPopover from "./ordonnance/AddExamPopover";

const FILTER_TABS = [
  { key: "all", label: "Toutes" },
  { key: "validation", label: "À valider" },
  { key: "errors", label: "Erreurs" },
];

const categoryOf = (task) => {
  if (task.type === "SYNONYM_SUGGESTION") return "suggestion";
  if (task.kind === "virtual") return "pending";
  return "error";
};

const TaskSidebar = ({ isOpen, onClose }) => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [refusedTaskId, setRefusedTaskId] = useState(null);
  const [correctedSuggestion, setCorrectedSuggestion] = useState(null);
  const listRef = useRef(null);
  const history = useHistory();
  const queryClient = useQueryClient();

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ["tasks"],
    queryFn: async () => {
      const response = await API.get("/task");
      if (!response.ok) {
        const error = new Error(response.message);
        capture(error);
        throw error;
      }
      return response.data;
    },
    enabled: isOpen,
    refetchInterval: 60_000,
  });

  const updateTaskMutation = useMutation({
    mutationFn: async ({ id, body }) => {
      const response = await API.put(`/task/${id}`, body);
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    onMutate: async ({ id, body }) => {
      await queryClient.cancelQueries({ queryKey: ["tasks"] });
      await queryClient.cancelQueries({ queryKey: ["taskCount"] });
      const previousTasks = queryClient.getQueryData(["tasks"]);
      const previousCount = queryClient.getQueryData(["taskCount"]);
      const isRestore = body?.status === "OPEN";
      queryClient.setQueryData(["tasks"], (old) =>
        isRestore ? (old || []).map((t) => (t._id === id ? { ...t, status: "OPEN" } : t)) : (old || []).filter((t) => t._id !== id),
      );
      queryClient.setQueryData(["taskCount"], (old) => {
        if (!old) return old;
        return { count: isRestore ? old.count + 1 : Math.max(0, old.count - 1) };
      });
      return { previousTasks, previousCount };
    },
    onError: (error, _variables, context) => {
      queryClient.setQueryData(["tasks"], context?.previousTasks);
      queryClient.setQueryData(["taskCount"], context?.previousCount);
      capture(error);
      toast.error("Erreur lors de la mise à jour de la tâche");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
    },
  });

  const acceptSuggestionMutation = useMutation({
    mutationFn: async (task) => {
      const response = await API.post(`/table/${task.metadata.tableId}/exam`, { value: task.metadata.label, type: task.metadata.examType, source: "SUGGESTION" });
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    onSuccess: (_exam, task) => {
      toast.success(`« ${task.metadata.label} » ajouté à ${task.metadata.code}`);
      queryClient.invalidateQueries({ queryKey: ["tables"] });
    },
    onError: (error) => {
      capture(error);
      toast.error(error.message || "Erreur lors de l'ajout du libellé");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
    },
  });

  const filteredTasks = useMemo(() => {
    const priority = { error: 0, suggestion: 1, pending: 2 };
    const filtered = tasks.filter((task) => {
      const isDismissed = task.status === "DISMISSED";
      const category = categoryOf(task);
      if (activeFilter === "dismissed") return isDismissed;
      if (isDismissed) return false;
      if (activeFilter === "validation" && category === "error") return false;
      if (activeFilter === "errors" && category !== "error") return false;
      if (searchQuery) return task.title.toLowerCase().includes(searchQuery.toLowerCase());
      return true;
    });
    filtered.sort((a, b) => priority[categoryOf(a)] - priority[categoryOf(b)] || (b.metadata?.coveredCount || 0) - (a.metadata?.coveredCount || 0));
    return filtered;
  }, [tasks, activeFilter, searchQuery]);

  const virtualizer = useVirtualizer({
    count: filteredTasks.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 64,
    overscan: 10,
    getItemKey: (index) => `${filteredTasks[index]?.kind}-${filteredTasks[index]?._id}`,
    measureElement: (element) => element?.getBoundingClientRect().height ?? 64,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalHeight = virtualizer.getTotalSize();

  const handleDismiss = useCallback((e, task) => {
    e.stopPropagation();
    if (task.kind === "task") {
      updateTaskMutation.mutate({ id: task._id, body: { status: "DISMISSED" } });
    }
  }, [updateTaskMutation]);

  const handleRestore = useCallback((e, task) => {
    e.stopPropagation();
    if (task.kind === "task") {
      updateTaskMutation.mutate({ id: task._id, body: { status: "OPEN" } });
    }
  }, [updateTaskMutation]);

  const onSuggestionAccept = (event, task) => {
    event.stopPropagation();
    if (task.metadata.tableId) acceptSuggestionMutation.mutate(task);
    else history.push(task.cta.link);
  };

  const onRefusalToggle = (event, task) => {
    event.stopPropagation();
    setRefusedTaskId(refusedTaskId === task._id ? null : task._id);
  };

  const onWrongCodeClick = (event, task) => {
    event.stopPropagation();
    setCorrectedSuggestion({ act: { _id: task._id, text: task.metadata.label, codes: [], codeType: task.metadata.examType }, anchor: event.currentTarget });
  };

  const onNotAnExamClick = (event, task) => {
    event.stopPropagation();
    updateTaskMutation.mutate({ id: task._id, body: { status: "DISMISSED", metadata: { ...task.metadata, outcome: "NOT_AN_EXAM" } } });
  };

  const onSuggestionCorrected = () => {
    setCorrectedSuggestion(null);
    queryClient.invalidateQueries({ queryKey: ["tasks"] });
    queryClient.invalidateQueries({ queryKey: ["taskCount"] });
  };

  const handleRowClick = useCallback((task) => {
    const link = task.cta?.link || task.editLink;
    if (!link) return;
    if (task.cta?.isExternal === true) {
      window.open(link, "_blank");
    } else {
      history.push(link);
    }
  }, [history]);

  const onSeePrescriptionsClick = (e, task) => {
    e.stopPropagation();
    history.push(`/prescription?q=${encodeURIComponent(`@suggestion:${task._id}`)}`);
  };

  const hasActiveFilter = activeFilter !== "all" || searchQuery.length > 0;

  const handleClearFilters = () => {
    setActiveFilter("all");
    setSearchQuery("");
  };

  const { isRealtimeDown } = useSocket();

  if (!isOpen) return null;

  const headerHeight = 56 + (IS_BETA_HOST ? 32 : 0) + (isRealtimeDown ? 32 : 0);

  return (
    <div className="fixed inset-y-0 right-0 z-40 flex flex-row-reverse" style={{ top: headerHeight }}>
      <div className="w-[380px] bg-white border-l border-gray-200 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-gray-900">Tâches</h2>
            {!isLoading && tasks.length > 0 && (
              <span className="text-[11px] font-medium text-gray-400 tabular-nums">{filteredTasks.length}</span>
            )}
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-500 transition-colors">
            <HiOutlineX className="w-4 h-4" />
          </button>
        </div>

        <div className="px-3 pt-2 pb-1.5 space-y-1.5 border-b border-gray-200">
          <div className="relative">
            <HiOutlineSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher..."
              className="w-full pl-8 pr-3 py-1.5 text-[13px] bg-gray-50 rounded-md border border-gray-200 outline-none placeholder:text-gray-400 focus:border-primary-300 focus:bg-white transition-colors"
            />
          </div>

          <div className="flex items-center gap-0.5">
            {FILTER_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => { setActiveFilter(tab.key); listRef.current?.scrollTo(0, 0); }}
                className={`px-2.5 py-1 text-[12px] font-medium rounded-md transition-colors ${
                  activeFilter === tab.key ? "bg-primary-50 text-primary-600" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => { setActiveFilter(activeFilter === "dismissed" ? "all" : "dismissed"); listRef.current?.scrollTo(0, 0); }}
              className={`px-2.5 py-1 text-[12px] font-medium rounded-md transition-colors ${
                activeFilter === "dismissed" ? "bg-primary-50 text-primary-600" : "text-gray-400 hover:text-gray-500 hover:bg-gray-50"
              }`}
            >
              Ignorées
            </button>
          </div>
        </div>

        <div ref={listRef} className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
            </div>
          )}

          {!isLoading && filteredTasks.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 px-4 gap-2">
              {hasActiveFilter ? (
                <>
                  <p className="text-[13px] text-gray-400">Aucune tâche dans cette catégorie</p>
                  <button type="button" onClick={handleClearFilters} className="text-[12px] text-primary-600 hover:text-primary-700 transition-colors">
                    Effacer les filtres
                  </button>
                </>
              ) : (
                <>
                  <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center">
                    <HiOutlineCheck className="w-4 h-4 text-gray-300" />
                  </div>
                  <p className="text-[13px] text-gray-400">Tout est à jour</p>
                </>
              )}
            </div>
          )}

          {!isLoading && filteredTasks.length > 0 && (
            <div style={{ height: totalHeight, position: "relative" }}>
              {virtualItems.map((virtualRow) => {
                const task = filteredTasks[virtualRow.index];
                const category = categoryOf(task);
                const isPersistent = task.kind === "task";
                const isDismissed = task.status === "DISMISSED";
                const taskKey = `${task.kind}-${task._id}`;
                const hasLink = task.cta?.link || task.editLink;
                const meta = task.metadata || {};
                const isSuggestion = category === "suggestion" && meta.matchValue;
                const isHarvestedSuggestion = isSuggestion && Boolean(meta.source) && !isDismissed;
                return (
                  <div
                    key={taskKey}
                    data-index={virtualRow.index}
                    ref={virtualizer.measureElement}
                    style={{ position: "absolute", top: 0, left: 0, right: 0, transform: `translateY(${virtualRow.start}px)` }}
                    className={`group border-b border-gray-100 ${hasLink ? "cursor-pointer hover:bg-gray-50" : ""}`}
                    onClick={hasLink ? () => handleRowClick(task) : undefined}
                  >
                    <div className="flex items-start gap-2.5 px-3 py-2.5">
                      {category === "suggestion" ? (
                        <HiOutlineTag className="mt-px h-4 w-4 shrink-0 text-primary-500" />
                      ) : (
                        <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${category === "error" ? "bg-red-500" : "bg-orange-400"}`} />
                      )}

                      <div className="min-w-0 flex-1">
                        {isSuggestion ? (
                          <>
                            <p className="truncate text-[13px] font-medium text-gray-900">{meta.label || meta.matchValue}</p>
                            <div className="mt-1 flex min-w-0 items-center gap-1.5">
                              <span className="text-gray-300">→</span>
                              <span className="shrink-0 rounded bg-primary-50 px-1.5 py-0.5 text-[11px] font-medium text-primary-600">{meta.code}</span>
                              {meta.codeName && <span className="truncate text-[11px] text-gray-500">{meta.codeName}</span>}
                            </div>
                            {meta.coveredCount != null && (
                              <p className="mt-1 text-[11px] text-gray-400">
                                {meta.tableId ? "Aurait codé" : "Table à créer · aurait codé"} <span className="tabular-nums">{meta.coveredCount}</span> ordonnance
                                {meta.coveredCount > 1 ? "s" : ""} sur 90 jours
                                {meta.addedCount != null && meta.coveredCount > 0 && (
                                  <span className="whitespace-nowrap">
                                    {" · "}
                                    <span className="tabular-nums">{Math.round((100 * Math.min(meta.addedCount, meta.coveredCount)) / meta.coveredCount)} %</span> de confiance
                                  </span>
                                )}
                              </p>
                            )}
                            {meta.coveredMentions?.length > 0 && (
                              <p className="mt-0.5 truncate text-[11px] text-gray-400" title={meta.coveredMentions.join(", ")}>
                                Couvre aussi : {meta.coveredMentions.join(", ")}
                              </p>
                            )}
                            {isHarvestedSuggestion && (
                              <button type="button" onClick={(e) => onSeePrescriptionsClick(e, task)} className="mt-1 text-[11px] font-medium text-primary-600 hover:underline">
                                Voir les ordonnances
                              </button>
                            )}
                            {isHarvestedSuggestion && refusedTaskId === task._id && (
                              <div className="mt-2 flex items-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={(e) => onWrongCodeClick(e, task)}
                                  className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600 transition-colors hover:bg-gray-50"
                                >
                                  Mauvais code
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => onNotAnExamClick(e, task)}
                                  className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600 transition-colors hover:bg-gray-50"
                                >
                                  Pas un examen
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="block break-words text-[13px] leading-snug text-gray-700">{task.title}</span>
                        )}
                      </div>

                      <div className="flex shrink-0 items-center gap-0.5">
                        {isDismissed && (
                          <button
                            type="button"
                            title="Restaurer"
                            onClick={(e) => handleRestore(e, task)}
                            className="rounded p-1 text-gray-400 transition-colors hover:bg-primary-50 hover:text-primary-500"
                          >
                            <HiOutlineRewind className="h-3.5 w-3.5" />
                          </button>
                        )}

                        {isHarvestedSuggestion && (
                          <button
                            type="button"
                            title={meta.tableId ? "Valider" : "Créer la table"}
                            onClick={(e) => onSuggestionAccept(e, task)}
                            disabled={acceptSuggestionMutation.isPending}
                            className="rounded p-1 text-gray-300 transition-colors hover:bg-primary-50 hover:text-primary-600 disabled:opacity-50"
                          >
                            <HiOutlineCheck className="h-3.5 w-3.5" />
                          </button>
                        )}

                        {isPersistent && !isDismissed && (
                          <button
                            type="button"
                            title={isHarvestedSuggestion ? "Refuser" : "Ignorer"}
                            onClick={(e) => (isHarvestedSuggestion ? onRefusalToggle(e, task) : handleDismiss(e, task))}
                            className="rounded p-1 text-gray-300 transition-colors hover:bg-red-50 hover:text-red-500"
                          >
                            <HiOutlineX className="h-3.5 w-3.5" />
                          </button>
                        )}

                        {hasLink && task.cta?.isExternal === true && <HiOutlineExternalLink className="h-3.5 w-3.5 text-gray-300" />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex-1" onClick={onClose} />

      {correctedSuggestion && (
        <AddExamPopover
          act={correctedSuggestion.act}
          referenceElement={correctedSuggestion.anchor}
          source="SUGGESTION"
          onClose={() => setCorrectedSuggestion(null)}
          onAdded={onSuggestionCorrected}
        />
      )}
    </div>
  );
};

export default TaskSidebar;
